import { Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Spinner } from "react-bootstrap";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRevenueByDeliveryStatus, RevenueByDeliveryStatusItem } from "slices/Dashboard/Revenue_By_Delivery_Status/thunk";
import { WarehouseOption } from "./DashboardFilters";
import "./TopMetrics.css";

interface RevenueByDeliveryStatusProps {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: WarehouseOption[];
}

export function RevenueByDeliveryStatus({ dateFrom, dateTo, warehouse = [] }: RevenueByDeliveryStatusProps) {
  const dispatch = useDispatch<any>();
  const { data, loading } = useSelector((state: any) => state.RevenueByDeliveryStatusReducer);

  // Fetch revenue by delivery status data when filters change
  useEffect(() => {
    dispatch(
      fetchRevenueByDeliveryStatus({
        dateFrom,
        dateTo,
        warehouse,
      })
    ).catch((error: any) => {
      console.error("Error fetching revenue by delivery status:", error);
    });
  }, [dispatch, dateFrom, dateTo, warehouse]);

  // Use data from API or default empty data
  const revenueByStatusData: RevenueByDeliveryStatusItem[] = data?.data || [];

  return (
    <div className={`revenue-card position-relative overflow-hidden ${loading ? 'metric-card-loading' : ''}`}>
      <div className="metric-corner-decoration"></div>
      <div className="revenue-header">
        <div className="icon-container icon-container-indigo">
          <Package className="icon text-indigo" />
        </div>
        <span className="metric-label">Revenue by Delivery Status</span>
      </div>
      
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '180px' }}>
          <Spinner animation="border" variant="primary" size="sm" />
        </div>
      ) : revenueByStatusData.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '180px' }}>
          <span className="text-muted">No data available</span>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={revenueByStatusData as any}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                type="number" 
                tick={{ fontSize: 11 }}
                tickFormatter={(value: number) => `₹${(value / 100000).toFixed(1)}L`}
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={100}
                tick={{ fontSize: 11 }}
              />
              <Tooltip 
                formatter={(value: number) => [`₹${(value / 100000).toFixed(2)}L`, 'Revenue']}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '8px 12px',
                }}
              />
              <Bar 
                dataKey="value" 
                radius={[0, 8, 8, 0]}
              >
                {revenueByStatusData.map((entry: RevenueByDeliveryStatusItem, index: number) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          
          <div className="revenue-legend" style={{ marginTop: '8px' }}>
            {revenueByStatusData.map((item: RevenueByDeliveryStatusItem, index: number) => (
              <div key={index} className="revenue-legend-item">
                <div className="revenue-legend-content">
                  <div 
                    className="color-dot" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="metric-label">{item.name}</span>
                </div>
                <span className="metric-label text-gray-dark">₹{(item.value / 100000).toFixed(2)}L</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

